<template>
  <b-form @submit.prevent="submit">
    <b-row>
      <b-col md="6">
        <b-form-group label="نام قرارداد" label-for="contract-name">
          <b-form-input
            id="contract-name"
            v-model.trim="form.name"
            :state="fieldState('name')"
            maxlength="255"
            placeholder="نام قرارداد را وارد کنید"
          />
          <b-form-invalid-feedback>{{ fieldError('name') }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="نوع قرارداد" label-for="contract-type">
          <v-select
            id="contract-type"
            v-model="form.type"
            :options="contractTypes"
            :reduce="option => option.value"
            :clearable="false"
            label="label"
          />
          <small v-if="fieldError('type')" class="text-danger">{{ fieldError('type') }}</small>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="نوع طرف قرارداد" label-for="counterparty-type">
          <v-select
            id="counterparty-type"
            v-model="form.counterparty_type"
            :options="counterpartyTypes"
            :reduce="option => option.value"
            :clearable="false"
            label="label"
          />
          <small v-if="fieldError('counterparty_type')" class="text-danger">{{ fieldError('counterparty_type') }}</small>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="6">
        <b-form-group label="طرف قرارداد" label-for="counterparty">
          <b-form-input
            id="counterparty"
            v-model.trim="form.counterparty"
            :state="fieldState('counterparty')"
            maxlength="500"
            placeholder="نام شخص یا شرکت را وارد کنید"
          />
          <b-form-invalid-feedback>{{ fieldError('counterparty') }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="تاریخ انقضا" label-for="expires-at">
          <date-picker
            id="expires-at"
            v-model="form.expires_at"
            type="date"
            display-format="jDD jMMMM jYYYY"
            format="YYYY-MM-DD"
            :class="{ 'is-invalid': fieldError('expires_at') }"
            placeholder="تاریخ انقضا را انتخاب کنید"
          />
          <small v-if="fieldError('expires_at')" class="text-danger">{{ fieldError('expires_at') }}</small>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="دوره یادآوری (روز)" label-for="reminder-lead-days">
          <b-form-input
            id="reminder-lead-days"
            v-model.number="form.reminder_lead_days"
            :state="fieldState('reminder_lead_days')"
            type="number"
            min="0"
            step="1"
          />
          <b-form-invalid-feedback>{{ fieldError('reminder_lead_days') }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-button type="submit" variant="primary" :disabled="saving">
      <b-spinner v-if="saving" small class="mr-50" />
      {{ submitLabel }}
    </b-button>
  </b-form>
</template>

<script>
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import VSelect from 'vue-select'

export default {
  name: 'ContractForm',
  components: {
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BRow,
    BSpinner,
    VSelect,
  },
  props: {
    contract: {
      type: Object,
      default: () => ({}),
    },
    saving: {
      type: Boolean,
      default: false,
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
    submitLabel: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: this.createForm(this.contract),
      validationErrors: {},
      contractTypes: [
        { label: 'داخلی', value: 'INTERNAL' },
        { label: 'خارجی', value: 'EXTERNAL' },
      ],
      counterpartyTypes: [
        { label: 'شخص', value: 'PERSON' },
        { label: 'شرکت', value: 'COMPANY' },
      ],
    }
  },
  watch: {
    contract: {
      deep: true,
      handler(contract) {
        this.form = this.createForm(contract)
      },
    },
  },
  methods: {
    createForm(contract) {
      return {
        name: contract.name || '',
        type: contract.type && contract.type.key ? contract.type.key : (contract.type || null),
        counterparty: contract.counterparty || '',
        counterparty_type: contract.counterpartyType && contract.counterpartyType.key
          ? contract.counterpartyType.key
          : (contract.counterparty_type || contract.counterpartyType || null),
        expires_at: contract.expires_at || contract.expiration_date || null,
        reminder_lead_days: contract.reminderLeadDays !== undefined
          ? contract.reminderLeadDays
          : (contract.reminder_lead_days !== undefined ? contract.reminder_lead_days : 30),
      }
    },
    fieldError(field) {
      const errors = this.validationErrors[field] || this.serverErrors[field]
      return errors && errors.length ? errors[0] : ''
    },
    fieldState(field) {
      return this.fieldError(field) ? false : null
    },
    validate() {
      const errors = {}
      if (!this.form.name) errors.name = ['نام قرارداد الزامی است.']
      if (!this.form.type) errors.type = ['نوع قرارداد الزامی است.']
      if (!this.form.counterparty) errors.counterparty = ['طرف قرارداد الزامی است.']
      if (!this.form.counterparty_type) errors.counterparty_type = ['نوع طرف قرارداد الزامی است.']
      if (!this.form.expires_at) errors.expires_at = ['تاریخ انقضا الزامی است.']
      if (this.form.reminder_lead_days === null || this.form.reminder_lead_days === '' || this.form.reminder_lead_days < 0) {
        errors.reminder_lead_days = ['دوره یادآوری باید صفر یا بیشتر باشد.']
      }
      this.validationErrors = errors
      return Object.keys(errors).length === 0
    },
    submit() {
      if (this.validate()) this.$emit('submit', { ...this.form })
    },
  },
}
</script>